<script>
import { mapActions, mapState } from 'vuex';

export default {
  name: 'ItineraryPricesCarRentalSearchBar',
  props: {
    selectedPriceInterval: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      defaultRequest: {
        priceIntervalId: null,
        rentalId: null,
        fromDay: null,
        days: null,
        grp: null,
        pax: null,
        included: null,
        supplier: null,
        sheet: null,
      },
      priceRequest: { ...this.defaultRequest },
      carRentalStartDays: [],
      carRentalNoOfDays: [],
      carRentalNoOfPax: [],
      carRentalTypes: [{ id: 0, name: 'Optional' }, { id: 1, name: 'Included' }],
    };
  },
  watch: {
    selectedPriceInterval: {
      immediate: true,
      handler() {
        this.priceRequest = {
          ...this.defaultRequest,
        };
      },
    },
  },
  computed: {
    ...mapState({
      carSuppliers: state => state.itinerary.carSuppliers.items,
      carSheets: state => state.itinerary.carSheets.items,
      dayProgram: state => state.itinerary.pricesDayProgram.items,
      carGroups: state => state.itinerary.carGroups.items,
    }),
    saveDisabled() {
      return !this.priceRequest.rentalId
        || !this.priceRequest.fromDay
        || !this.priceRequest.days
        || !this.priceRequest.grp
        || !this.priceRequest.pax
        || this.priceRequest.included === null;
    },
  },
  methods: {
    ...mapActions({
      getCarRentalSheetsBySupplierId: 'itinerary/getCarRentalSheetsBySupplierId',
      getCarRentalSheetsBySupplierIdAndDate: 'itinerary/getCarRentalSheetsBySupplierIdAndDate',
      getCarRentalGroupsByDate: 'itinerary/getCarRentalGroupsByDate',
      addCarRentalPriceInterval: 'itinerary/addCarRentalPriceInterval',
    }),
    setSupplier(value) {
      if (!value) {
        this.priceRequest = { ...this.defaultRequest };
        return;
      }

      this.priceRequest.supplier = value;
      this.getCarRentalSheetsBySupplierIdAndDate({
        supplierId: value.id,
        startDate: this.selectedPriceInterval.arrivalFrom,
      });
      this.setStartDays();
      this.setNoOfPax();
      this.priceRequest.priceIntervalId = this.selectedPriceInterval.id;
    },
    setSheet(value) {
      this.priceRequest.sheet = value;
      this.priceRequest.rentalId = typeof value !== 'undefined' ? value.id : null;
    },
    setStartDays() {
      this.carRentalStartDays = Array.from({ length: this.dayProgram.length }, (value, index) => index + 1);
    },
    setStartDay(value) {
      this.carRentalNoOfDays = Array.from({
        length: (this.dayProgram.length - (value - 1)) },
      (value2, index) => index + 1);

      this.priceRequest.fromDay = value;
      const startDate = this.$moment(this.selectedPriceInterval.arrivalFrom);
      const rentalStartDate = startDate.add(value - 1, 'days').format('YYYY-MM-DD');
      this.getCarRentalGroupsByDate({ rentalId: this.priceRequest.rentalId, startDate: rentalStartDate });
    },
    setNoOfPax() {
      this.carRentalNoOfPax = Array.from({ length: 20 }, (value, index) => index + 1);
    },
    setDays(value) {
      this.priceRequest.days = typeof value !== 'undefined' ? value : null;
    },
    setGrp(value) {
      this.priceRequest.grp = typeof value !== 'undefined' ? value.id : null;
    },
    setPax(value) {
      this.priceRequest.pax = typeof value !== 'undefined' ? value : null;
    },
    setType(value) {
      this.priceRequest.included = typeof value !== 'undefined' ? value.id : null;
    },
    addCarToInterval() {
      const priceRequest = {
        ...this.priceRequest,
      };
      this.addCarRentalPriceInterval({
        priceRequest,
        priceIntervalId: this.selectedPriceInterval.id,
      }).then(() => {
        this.$emit('save');
      });
    },
  },
};
</script>

<template>
    <div>
      <h2>Search and add car rental</h2>
      <v-layout class="mt-4">
        <v-flex class="mr-2 xs2">
          <v-select
            :items="carSuppliers"
            :value="priceRequest.supplier"
            :disabled="!carSuppliers.length"
            label="Supplier"
            item-text="name"
            item-value="id"
            filled
            clearable
            return-object
            class="mr-2"
            @change="setSupplier"
          />
        </v-flex>
        <v-flex class="mr-2 xs4">
          <v-select
            :items="carSheets"
            :value="priceRequest.sheet"
            :disabled="!carSheets.length"
            label="Sheet"
            item-text="sheet"
            item-value="id"
            return-object
            class="mr-2"
            @change="setSheet"
          />
        </v-flex>
        <v-flex class="mr-1 xs1">
          <v-select
            :items="carRentalStartDays"
            :value="priceRequest.fromDay"
            :disabled="!carRentalStartDays.length"
            label="Start day"
            class="mr-2"
            item-text="name"
            item-value="id"
            return-object
            @change="setStartDay($event)"
          />
        </v-flex>
        <v-flex class="mr-1 xs1">
          <v-select
            :items="carRentalNoOfDays"
            :value="priceRequest.days"
            :disabled="!carRentalNoOfDays.length"
            label="No. of days"
            class="mr-2"
            item-text="name"
            item-value="id"
            return-object
            @change="setDays"
          />
        </v-flex>
        <v-flex class="mr-1 xs1">
          <v-select
            :items="carGroups"
            :value="priceRequest.grp"
            :disabled="!carGroups.length"
            label="Group"
            class="mr-2"
            item-text="name"
            item-value="id"
            return-object
            @change="setGrp"
          />
        </v-flex>
        <v-flex class="mr-1 xs1">
          <v-select
            :items="carRentalNoOfPax"
            :value="priceRequest.pax"
            :disabled="!carRentalNoOfPax.length"
            label="Pax"
            class="mr-2"
            item-text="name"
            item-value="id"
            return-object
            @change="setPax"
          />
        </v-flex>
        <v-flex class="mr-1 xs1">
          <v-select
            :items="carRentalTypes"
            :value="priceRequest.included"
            :disabled="!carRentalTypes.length"
            label="Type"
            class="mr-2"
            item-text="name"
            item-value="id"
            return-object
            @change="setType"
          />
        </v-flex>
        <v-flex class="mr-2">
          <v-btn
            :disabled="saveDisabled"
            color="success"
            label="Button"
            @click="addCarToInterval()"
          >
            Add car rental
          </v-btn>
        </v-flex>
      </v-layout>
    </div>
</template>



// WEBPACK FOOTER //
// src/components/itinerary/itineraryPrices/ItineraryPricesCarRentalSearchBar.vue